import * as THREE from 'three';
import { PILOT_HEAD } from '../core/SimConfig.js';
import { clamp, lerp, rand, DEG2RAD, X_AXIS, Y_AXIS, Z_AXIS } from '../utils.js';

// Where the pilot is looking. The scheduler moves between these; each one
// resolves to a yaw/pitch look direction in the aircraft body frame.
const Gaze = Object.freeze({
  FORWARD: 0,      // out the front, wandering a little
  INSTRUMENTS: 1,  // down at a gauge
  SCAN: 2,         // clearing turn / check six
  TRACK: 3,        // eyes on an enemy
  THREAT: 4,       // eyes on an inbound missile
});

const _m = new THREE.Matrix4();
const _box = new THREE.Box3();
const _rot = new THREE.Quaternion();
const _qYaw = new THREE.Quaternion();
const _qPitch = new THREE.Quaternion();
const _qRoll = new THREE.Quaternion();
const _invQuat = new THREE.Quaternion();
const _angBody = new THREE.Vector3();
const _dir = new THREE.Vector3();

/**
 * Procedural head animation for the seated pilot mesh.
 *
 * One scalar — `arousal` (0..1) — is summed from how dangerous the current
 * second of flight is (G, body rates, stall, guns, inbound missiles, enemy
 * proximity, low altitude). It drives three things at once: how fast the head
 * snaps between look points, how much involuntary jitter rides on top, and how
 * *short* each dwell is. Because the dwells compress, a calm cruise reads as
 * the odd unhurried glance at a gauge and a gunfight as a head that never stops
 * working — without authoring two separate behaviours.
 *
 * Costs one quaternion write per frame. The enemy list is only scanned every
 * `PILOT_HEAD.scanInterval`, and the whole thing is skipped whenever the pilot
 * isn't drawn — the cockpit view, and the action-camera mounts that don't ask
 * for him.
 */
export default class PilotHead {
  constructor({ aircraft, model, cameraManager }) {
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;

    // Gameplay-only references, attached by App after construction — the
    // start-screen preview runs without them and simply stays calm.
    this.weaponSystem = null;
    this.enemyManager = null;
    this.enemyMissileSystem = null;
    this.maverickSeeker = null;

    const cfg = PILOT_HEAD;
    const mesh = model.getObjectByName(cfg.mesh);
    if (!mesh) {
      console.warn(`PilotHead: mesh "${cfg.mesh}" not found`);
      this.head = null;
      return;
    }

    // Preserve the rest pose across scenario reloads (the same model object is
    // reused), as the other cockpit animators do.
    if (!mesh.userData._originalQuat) {
      mesh.userData._originalQuat = mesh.quaternion.clone();
      mesh.userData._originalPos = mesh.position.clone();
    }
    this.head = mesh;
    this.baseQuat = mesh.userData._originalQuat.clone();
    this.basePos = mesh.userData._originalPos.clone();
    mesh.quaternion.copy(this.baseQuat);
    mesh.position.copy(this.basePos);

    // Neck hinge, in the mesh's parent space. The authored origin sits up near
    // the crown, so rotating about it would swing the chin through the canopy;
    // derive a hinge from the head's own bounds instead (low, and aft of
    // centre, roughly where the skull meets the spine).
    this.pivot = new THREE.Vector3();
    const geo = mesh.geometry;
    if (geo) {
      if (!geo.boundingBox) geo.computeBoundingBox();
      _m.compose(this.basePos, this.baseQuat, mesh.scale);
      _box.copy(geo.boundingBox).applyMatrix4(_m);
      this.pivot.set(
        (_box.min.x + _box.max.x) * 0.5,
        lerp(_box.min.y, _box.max.y, cfg.pivotHeightFrac),
        // Forward is -Z, so min.z is the face and max.z the back of the skull.
        lerp(_box.min.z, _box.max.z, cfg.pivotDepthFrac),
      );
    } else {
      this.pivot.copy(this.basePos);
    }

    // ── Gaze state ──────────────────────────────────────────────────────
    this.arousal = 0;
    this.state = Gaze.FORWARD;
    this._dwell = rand(cfg.glance.forwardDwellCalm[0], cfg.glance.forwardDwellCalm[1]);
    this._aimYaw = 0;       // look point for the static gaze states
    this._aimPitch = 0;
    this._yaw = 0;          // spring state (radians, mesh parent space)
    this._pitch = 0;
    this._roll = 0;
    this._yawVel = 0;
    this._pitchVel = 0;
    this._rollVel = 0;
    this._time = rand(0, 100);  // noise phase — no two spawns breathe in sync
    this._scanTimer = 0;
    this._candidate = null; // best enemy to look at, refreshed on scan
    this._target = null;    // the one currently being tracked
    this._threat = null;    // inbound missile, if any
    this._enemyProx = 0;    // 0..1 proximity of the nearest live enemy
    this._gy = 1;           // smoothed body G (the raw value buzzes)
    this._gx = 0;
    this._visible = false;
  }

  /** True while the pilot mesh is actually drawn. App sets his visibility off
   *  the same flag, so the gate matches the draw exactly — including the
   *  mid-transition stretch where he's still up because the camera hasn't
   *  reached the canopy yet. */
  _onScreen() {
    const cm = this.cameraManager;
    if (!cm) return true;
    return cm.pilotVisible;
  }

  update(dt) {
    if (!this.head || dt <= 0) return;

    if (!this._onScreen()) {
      this._visible = false;
      return;
    }
    if (!this._visible) {
      // Coming back into view — re-decide rather than resume a stale glance.
      this._visible = true;
      this._scanTimer = 0;
      this._dwell = 0;
    }

    const cfg = PILOT_HEAD;
    const ac = this.aircraft;
    this._time += dt;

    // G is smoothed the way the cockpit camera smooths its own copy: the raw
    // per-frame value out of the integrator is noisy enough to buzz the head.
    const gLag = clamp(cfg.gSmoothing * dt, 0, 1);
    this._gy += (ac.gForceLocal.y - this._gy) * gLag;
    this._gx += (ac.gForceLocal.x - this._gx) * gLag;

    // Body-frame angular velocity — feeds both the arousal rate term and the
    // look-into-the-turn lead.
    _invQuat.copy(ac.quaternion).invert();
    _angBody.copy(ac.angularVelocity).applyQuaternion(_invQuat);

    this._scanTimer -= dt;
    if (this._scanTimer <= 0) {
      this._scanTimer = cfg.scanInterval;
      this._scanWorld();
    }

    if (ac.destroyed) {
      // Nobody is flying any more. Drop the head and leave it there.
      const l = cfg.limp;
      this.state = Gaze.FORWARD;
      this.arousal = 0;
      this._aimYaw = l.yawDeg * DEG2RAD;
      this._aimPitch = l.pitchDeg * DEG2RAD;
      this._stepSprings(dt, this._aimYaw, this._aimPitch, l.rollDeg * DEG2RAD, l.stiffness);
      this._apply(this._yaw, this._pitch, this._roll);
      return;
    }

    this._updateArousal(dt, _angBody);

    this._dwell -= dt;
    if (this._dwell <= 0) this._pickGaze();

    // Sustained G pins the neck: the usable range shrinks toward
    // `strainMinRange`, so under a hard pull the head locks ahead and strains
    // rather than swivelling freely.
    const gy = this._gy;
    const strain = clamp(
      1 - (Math.abs(gy) - cfg.strainStartG) / (cfg.strainFullG - cfg.strainStartG) * (1 - cfg.strainMinRange),
      cfg.strainMinRange, 1,
    );
    const yawLimit = cfg.yawLimitDeg * DEG2RAD * strain;
    const pitchUp = cfg.pitchUpLimitDeg * DEG2RAD * strain;
    const pitchDown = cfg.pitchDownLimitDeg * DEG2RAD * strain;
    const rollLimit = cfg.rollLimitDeg * DEG2RAD;

    // Resolve the active gaze into a look angle.
    let yaw = this._aimYaw;
    let pitch = this._aimPitch;
    if (this.state === Gaze.TRACK || this.state === Gaze.THREAT) {
      const obj = this.state === Gaze.THREAT ? this._threat : this._target;
      const pos = this._posOf(obj);
      if (!pos) {
        this._dwell = 0;   // lost it — re-decide next frame
      } else {
        _dir.copy(pos).sub(ac.position).applyQuaternion(_invQuat);
        const len = _dir.length();
        if (len > 1e-3) {
          _dir.multiplyScalar(1 / len);
          yaw = Math.atan2(-_dir.x, -_dir.z) * cfg.headFollow;
          pitch = Math.asin(clamp(_dir.y, -1, 1)) * cfg.headFollow;
        }
      }
    } else {
      // Lead the turn, the way a pilot looks into a bank — but only while the
      // gaze is free, not while it's pinned to something.
      const leadMax = cfg.turnLeadMaxDeg * DEG2RAD;
      yaw += clamp(_angBody.y * cfg.turnLeadGain, -leadMax, leadMax);
    }

    yaw = clamp(yaw, -yawLimit, yawLimit);
    pitch = clamp(pitch, -pitchDown, pitchUp);

    // The head tips into the direction it's looking, and away from lateral G.
    const roll = clamp(
      this._gx * cfg.gRollDegPerG * DEG2RAD + yaw * cfg.rollWithYaw,
      -rollLimit, rollLimit,
    );

    this._stepSprings(dt, yaw, pitch, roll, lerp(cfg.stiffnessCalm, cfg.stiffnessAlert, this.arousal));

    // Involuntary motion, layered on after the spring so it isn't smoothed
    // away: breathing and buffet when calm, a restless head when alert.
    const n = cfg.noise;
    const amp = lerp(n.ampCalmDeg, n.ampAlertDeg, this.arousal) * DEG2RAD;
    const f = lerp(n.freqCalm, n.freqAlert, this.arousal);
    const t = this._time;
    const nYaw = amp * (Math.sin(t * f) * 0.6 + Math.sin(t * f * 2.3 + 1.7) * 0.4);
    const nPitch = amp * 0.7 * (Math.sin(t * f * 0.83 + 2.4) * 0.6 + Math.sin(t * f * 1.9 + 0.5) * 0.4);
    // The helmet is heavy: positive G drags the chin down, negative lifts it.
    const sag = clamp(-(gy - 1) * cfg.gSagDegPerG, -cfg.gSagMaxDeg, cfg.gSagMaxDeg) * DEG2RAD;

    this._apply(
      clamp(this._yaw + nYaw, -yawLimit, yawLimit),
      clamp(this._pitch + nPitch + sag, -pitchDown, pitchUp),
      clamp(this._roll + nYaw * 0.5, -rollLimit, rollLimit),
    );
  }

  /** Semi-implicit Euler on three damped springs (unit mass), the same
   *  integrator the rest of the cockpit hardware uses. */
  _stepSprings(dt, yawT, pitchT, rollT, k) {
    const c = 2 * Math.sqrt(k) * PILOT_HEAD.dampingRatio;
    this._yawVel += (k * (yawT - this._yaw) - c * this._yawVel) * dt;
    this._yaw += this._yawVel * dt;
    this._pitchVel += (k * (pitchT - this._pitch) - c * this._pitchVel) * dt;
    this._pitch += this._pitchVel * dt;
    this._rollVel += (k * (rollT - this._roll) - c * this._rollVel) * dt;
    this._roll += this._rollVel * dt;
  }

  /** Write yaw/pitch/roll (mesh parent space) out, hinged at the neck. */
  _apply(yaw, pitch, roll) {
    _qYaw.setFromAxisAngle(Y_AXIS, yaw);
    _qPitch.setFromAxisAngle(X_AXIS, pitch);
    _qRoll.setFromAxisAngle(Z_AXIS, roll);
    _rot.copy(_qYaw).multiply(_qPitch).multiply(_qRoll);
    this.head.quaternion.copy(_rot).multiply(this.baseQuat);
    // Swing the authored origin about the hinge so the skull turns on the joint
    // rather than orbiting its own pivot point.
    this.head.position.copy(this.basePos).sub(this.pivot).applyQuaternion(_rot).add(this.pivot);
  }

  /** Position of a look target — enemies carry a `flight`, missiles don't. */
  _posOf(obj) {
    if (!obj || obj.dead || obj.destroyed) return null;
    return obj.flight?.position || obj.position || null;
  }

  // ── World scan (throttled) ───────────────────────────────────────────────
  // Picks the enemy worth looking at and the missile worth worrying about, and
  // measures how close the nearest live enemy is. Runs a few times a second, so
  // a road column's entity count barely registers.
  _scanWorld() {
    const cfg = PILOT_HEAD.arousal;
    const ac = this.aircraft;

    // Inbound missile — anything actually chasing the player.
    this._threat = null;
    const missiles = this.enemyMissileSystem?.activeMissiles;
    if (missiles) {
      let best = Infinity;
      for (let i = 0; i < missiles.length; i++) {
        const m = missiles[i];
        if (m.target !== ac) continue;
        const d = m.position.distanceToSquared(ac.position);
        if (d < best) { best = d; this._threat = m; }
      }
    }

    // Nearest live enemy (the proximity term) and the best one to look at:
    // close, and roughly where the neck can actually point.
    this._candidate = null;
    this._enemyProx = 0;
    const entities = this.enemyManager?.entities;
    if (entities && entities.length) {
      const fwd = ac.forwardVector;
      const fade = cfg.enemyFadeRange;
      let nearest = Infinity;
      let bestScore = 0;
      for (let i = 0; i < entities.length; i++) {
        const pos = this._posOf(entities[i]);
        if (!pos) continue;
        const dx = pos.x - ac.position.x;
        const dy = pos.y - ac.position.y;
        const dz = pos.z - ac.position.z;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d > fade) continue;
        if (d < nearest) nearest = d;
        // Behind the 3/9 line the neck can't get there anyway.
        const ahead = (dx * fwd.x + dy * fwd.y + dz * fwd.z) / (d || 1);
        if (ahead < -0.2) continue;
        const score = (1 - d / fade) * 0.6 + (ahead * 0.5 + 0.5) * 0.4;
        if (score > bestScore) { bestScore = score; this._candidate = entities[i]; }
      }
      if (nearest < Infinity) {
        this._enemyProx = 1 - clamp(
          (nearest - cfg.enemyFullRange) / (fade - cfg.enemyFullRange), 0, 1,
        );
      }
    }

    // A target that died mid-glance is dropped so the gaze re-decides instead
    // of staring at a wreck.
    if (this._target && !this._posOf(this._target)) this._target = null;
  }

  // ── Arousal ──────────────────────────────────────────────────────────────
  _updateArousal(dt, angBody) {
    const cfg = PILOT_HEAD.arousal;
    const ac = this.aircraft;

    let want = 0;
    want += Math.abs(this._gy - 1) * cfg.perG;
    want += (Math.abs(angBody.x) + Math.abs(angBody.z)) * cfg.perRadSec;
    if (ac.alpha > ac.stallAngle * cfg.stallWarnFrac) want += cfg.stall;
    if (this.weaponSystem?.firing) want += cfg.firing;
    if (this.maverickSeeker?.aiming) want += cfg.maverick;
    if (this._threat) want += cfg.threat;
    want += this._enemyProx * cfg.enemy;
    if (!ac.onGround && ac.altitudeAGL < cfg.lowAltitudeAGL) {
      want += (1 - ac.altitudeAGL / cfg.lowAltitudeAGL) * cfg.lowAltitude;
    }
    want = clamp(want, 0, 1);

    // Adrenaline rises fast and comes down slowly.
    const rate = want > this.arousal ? cfg.rise : cfg.fall;
    this.arousal += (want - this.arousal) * clamp(rate * dt, 0, 1);
  }

  // ── Gaze scheduling ──────────────────────────────────────────────────────
  _pickGaze() {
    const cfg = PILOT_HEAD;
    const g = cfg.glance;
    const a = this.arousal;
    const prev = this.state;

    // A missile in the air outranks everything else — but an unbroken stare at
    // it looks robotic, so eyes-on-the-missile alternates with a flick to the
    // threat scope and back out front.
    if (this._threat) {
      if (prev === Gaze.THREAT) {
        const r = Math.random();
        if (r < g.threatPanelChance) { this._enterPanel(cfg.lookRWR, g); return; }
        if (r < g.threatPanelChance + g.threatForwardChance) { this._enterForward(g, a); return; }
      }
      this.state = Gaze.THREAT;
      this._dwell = rand(g.threatDwell[0], g.threatDwell[1]);
      return;
    }

    // Working the trigger or the seeker: eyes stay on the job.
    if (this.weaponSystem?.firing) { this._enterTrackOrForward(g, a); return; }
    if (this.maverickSeeker?.aiming) { this._enterPanel(cfg.lookTV, g); return; }

    // Always come back to the forward field (or the target) after a glance
    // away, so the head never chains panel → over-the-shoulder.
    if (prev === Gaze.INSTRUMENTS || prev === Gaze.SCAN) {
      this._enterTrackOrForward(g, a);
      return;
    }

    const wInst = lerp(g.instrumentsWeightCalm, g.instrumentsWeightAlert, a);
    const wScan = lerp(g.scanWeightCalm, g.scanWeightAlert, a);
    const wTrack = this._candidate ? lerp(g.trackWeightCalm, g.trackWeightAlert, a) : 0;
    let r = Math.random() * (1 + wInst + wScan + wTrack);

    if ((r -= wInst) < 0) { this._enterPanel(this._pickPanelLook(cfg), g); return; }
    if ((r -= wScan) < 0) { this._enterScan(g, a); return; }
    if ((r -= wTrack) < 0) { this._enterTrack(g, a); return; }
    this._enterForward(g, a);
  }

  _enterTrack(g, a) {
    this.state = Gaze.TRACK;
    this._target = this._candidate;
    this._dwell = rand(
      lerp(g.trackDwellCalm[0], g.trackDwellAlert[0], a),
      lerp(g.trackDwellCalm[1], g.trackDwellAlert[1], a),
    );
  }

  _enterTrackOrForward(g, a) {
    if (this._candidate) this._enterTrack(g, a);
    else this._enterForward(g, a);
  }

  _enterForward(g, a) {
    this.state = Gaze.FORWARD;
    this._aimYaw = rand(-1, 1) * g.forwardDriftDeg * DEG2RAD;
    this._aimPitch = rand(-1, 1) * g.forwardDriftPitchDeg * DEG2RAD;
    this._dwell = rand(
      lerp(g.forwardDwellCalm[0], g.forwardDwellAlert[0], a),
      lerp(g.forwardDwellCalm[1], g.forwardDwellAlert[1], a),
    );
  }

  _enterPanel(look, g) {
    this.state = Gaze.INSTRUMENTS;
    const j = g.instrumentJitterDeg;
    this._aimYaw = (look.yawDeg + rand(-j, j)) * DEG2RAD * PILOT_HEAD.headFollow;
    this._aimPitch = (look.pitchDeg + rand(-j, j)) * DEG2RAD * PILOT_HEAD.headFollow;
    this._dwell = rand(g.instrumentDwell[0], g.instrumentDwell[1]);
  }

  _enterScan(g, a) {
    this.state = Gaze.SCAN;
    const side = Math.random() < 0.5 ? -1 : 1;
    if (a > g.checkSixArousal && Math.random() < g.checkSixChanceAlert) {
      // Check six — as far round as the harness allows, chin up.
      this._aimYaw = side * PILOT_HEAD.yawLimitDeg * DEG2RAD;
      this._aimPitch = g.checkSixPitchDeg * DEG2RAD;
    } else {
      this._aimYaw = side * rand(g.scanYawDeg[0], g.scanYawDeg[1]) * DEG2RAD;
      this._aimPitch = rand(g.scanPitchDeg[0], g.scanPitchDeg[1]) * DEG2RAD;
    }
    this._dwell = rand(g.scanDwell[0], g.scanDwell[1]);
  }

  /** Which gauge gets the glance: the threat scope when there's something out
   *  there, the gear lights while they cycle, else a primary flight gauge. */
  _pickPanelLook(cfg) {
    const ac = this.aircraft;
    if (this._candidate && Math.random() < cfg.rwrChanceThreat) return cfg.lookRWR;
    const gearMoving = Math.abs((ac.gearTarget ?? 0) - (ac.gear ?? 0)) > 0.01;
    if (gearMoving && Math.random() < cfg.gearChanceMoving) return cfg.lookGear;
    const looks = cfg.instrumentLooks;
    return looks[(Math.random() * looks.length) | 0];
  }

  dispose() {
    // Leave the head at its rest pose — the model object outlives the scenario.
    if (this.head) {
      this.head.quaternion.copy(this.baseQuat);
      this.head.position.copy(this.basePos);
    }
    this.head = null;
    this._candidate = null;
    this._target = null;
    this._threat = null;
  }
}
